// theory-gravity.js — genuine, DSO-free gravitation & galactic-dynamics tools
// for the Theory module (Frameworks domain).
//
// Reuse the SAME tested computations as the DSO comparison sims
// (theory-engines.js runMOND / runRAR / runDarkMatter / runHawking) but render
// ONLY the published physics - MOND (Milgrom 1983), the McGaugh radial
// acceleration relation (McGaugh, Lelli & Schombert 2016), the LCDM NFW dark
// matter halo (Navarro-Frenk-White 1997), Hawking black-hole thermodynamics
// (Hawking 1975). No DSO series, no DSO framing.
//
// Each tool shows the engine's computed data table AND a genuine GeoNum Engine
// Trust readout (drift-tracked precision on a representative GeoNum-computed
// quantity; Hawking uses the engine's own GeoNum arithmetic).

import { runRAR, runMOND, runDarkMatter, runHawking } from './theory-engines.js';
import { GeoNum } from './geo-num.js';
// The ONE client-side ladder (mirrors wasm/src/geonum.rs classify). Used instead
// of a local copy so a missing drift classifies to null, never to a verdict.
import { verdictFromDrift, verdictColor } from './gti-ladder.js';

const KPC = 3.086e19;
const charts = {};

function cssVar(name, fallback) {
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v || fallback;
}
function chart(id, datasets, title, xLabel, yLabel) {
  const cv = document.getElementById(id);
  if (!cv) return;
  if (charts[id]) charts[id].destroy();
  const txt = cssVar('--text', '#1e293b');
  const dim = cssVar('--text-dim', '#64748b');
  const grid = cssVar('--border', 'rgba(100,116,139,0.2)');
  charts[id] = new Chart(cv.getContext('2d'), {
    type: 'scatter',
    data: { datasets },
    options: {
      responsive: true, maintainAspectRatio: false, animation: false,
      plugins: {
        title: { display: true, text: title, color: txt, font: { size: 13 } },
        legend: { labels: { color: dim, boxWidth: 18 } },
      },
      scales: {
        x: { title: { display: true, text: xLabel, color: dim }, ticks: { color: dim }, grid: { color: grid } },
        y: { title: { display: true, text: yLabel, color: dim }, ticks: { color: dim }, grid: { color: grid } },
      },
    },
  });
}
function line(label, data, color, dash) {
  return { label, data, borderColor: color, backgroundColor: color, pointRadius: 0, showLine: true, borderWidth: 2, borderDash: dash || [] };
}
const num = (id, d) => { const el = document.getElementById(id); return el ? (parseFloat(el.value) || d) : d; };
const sel = (id, d) => { const el = document.getElementById(id); return el ? el.value : d; };
const setText = (id, t) => { const el = document.getElementById(id); if (el) el.textContent = t; };

const TRUST_NAMES = ['EXACT', 'PRECISE', 'VALID', 'DEGRADED', 'UNRELIABLE', 'CANCELLED'];
function trustColor(lvl) { return lvl <= 2 ? '#16a34a' : lvl === 3 ? '#d97706' : '#dc2626'; }

// GeoNum trust readout for a GeoNum-computed quantity.
function geonumBadge(g, label) {
  const lvl = g.getTrustLevel();
  const name = TRUST_NAMES[lvl] || '?';
  const rel = g.getRelativeUncertainty() * 100;
  const c = trustColor(lvl);
  return badge(name, c, `${label} = ${g.toFloat64().toPrecision(6)} · rel.unc ${rel.toExponential(2)}% · drift ${g.drift.toFixed(3)} · zone ${g.zone} · shade ${g.shade}`);
}
function badge(name, color, detail) {
  // Unified GTI (GeoNum Trust Index) two-segment marker - matches geonum-trust.js.
  return `<div style="display:flex;align-items:center;gap:10px;flex-wrap:wrap;margin-top:10px;font-size:12px;">
    <span style="display:inline-flex;align-items:center;" title="GeoNum Trust Index">
      <span style="background:#334155;color:#fff;padding:2px 8px;font-weight:700;border-radius:6px 0 0 6px;letter-spacing:.04em;font-size:11px;">GTI</span>
      <span style="background:${color}22;color:${color};padding:2px 10px;font-weight:700;border-radius:0 6px 6px 0;font-size:11px;border:1px solid ${color}66;border-left:none;">${name}</span>
    </span>
    <span style="color:var(--text-dim,#64748b);font-family:monospace;font-size:11px;">${detail}</span>
  </div>`;
}
function table(rows) {
  return '<table class="plasma-table" style="margin-top:10px;"><thead><tr><th>Quantity</th><th>Value</th></tr></thead><tbody>' +
    rows.map(r => `<tr><td>${r[0]}</td><td style="font-family:monospace;">${r[1]}</td></tr>`).join('') + '</tbody></table>';
}
function out(id, html) { const el = document.getElementById(id); if (el) el.innerHTML = html; }

// ---------------------------------------------------------------- MOND
function runMondTool(e) {
  e && e.preventDefault();
  const r = runMOND({
    mu_type: sel('tg-mond-mu', 'standard'), a0: num('tg-mond-a0', 1.2) * 1e-10,
    galaxy_mass: num('tg-mond-mass', 5) * 1e10, r_max: num('tg-mond-rmax', 50),
  });
  const c = r.rotation_curve, last = c[c.length - 1];
  chart('tg-mond-rot', [
    line('MOND (Milgrom)', c.map(p => ({ x: p.r_kpc, y: p.v_mond })), '#d97706'),
    line('Newtonian (baryons)', c.map(p => ({ x: p.r_kpc, y: p.v_newton })), '#dc2626', [5, 4]),
  ], 'Rotation curve', 'Radius (kpc)', 'v (km/s)');
  chart('tg-mond-muchart', [
    line('mu(x), ' + r.mu_type, r.mu_curve.map(p => ({ x: p.log_x, y: p.mu_mond })), '#2563eb'),
  ], 'Interpolation function', 'log10(x = g_N/a0)', 'mu(x)');
  // GeoNum-tracked MOND boost g/g_N at the outer radius (operands scaled to O(1)
  // so GeoNum stays well-conditioned; the ratio is scale-invariant).
  const boost = new GeoNum(last.g_mond * 1e10).divide(new GeoNum(last.g_newton * 1e10));
  out('tg-mond-out', table([
    ['Governing law', 'g = g_N / mu(g_N / a0)'],
    ['v_flat (MOND)', r.v_flat_mond + ' km/s'],
    ['v_flat (Newtonian, baryons)', last.v_newton.toFixed(1) + ' km/s'],
    ['a0', r.a0_display],
    ['Galaxy mass', r.galaxy_mass + ' x10^10 Msun'],
    ['mu(x) type', r.mu_type],
    ['Deep-MOND limit', 'v^4 = G M a0'],
    ['MOND boost g/g_N (outer R)', boost.toFloat64().toFixed(3) + 'x'],
  ]) + geonumBadge(boost, 'g_MOND/g_N'));
  setText('tg-mond-status', `v_flat = ${r.v_flat_mond} km/s | a0 = ${r.a0_display}`);
}

// ---------------------------------------------------------------- RAR
function runRarTool(e) {
  e && e.preventDefault();
  const a0 = num('tg-rar-a0', 1.2) * 1e-10;
  const r = runRAR({ a0_mond: a0, n_points: 120 });
  const d = r.data;
  chart('tg-rar-main', [
    line('McGaugh RAR', d.map(p => ({ x: p.log_gbar, y: p.log_g_rar })), '#0891b2'),
    line('MOND', d.map(p => ({ x: p.log_gbar, y: p.log_g_mond })), '#d97706', [5, 4]),
    line('Newtonian (1:1)', d.map(p => ({ x: p.log_gbar, y: p.log_g_newton })), '#64748b', [2, 2]),
  ], 'Radial Acceleration Relation', 'log10 g_bar (m/s^2)', 'log10 g_obs (m/s^2)');
  // 2nd chart: mass discrepancy g_obs/g_bar vs g_bar (the "missing mass" curve).
  chart('tg-rar-disc', [
    line('g_obs / g_bar (RAR)', d.map(p => ({ x: p.log_gbar, y: p.ratio_rar })), '#0891b2'),
    line('Newtonian = 1', d.map(p => ({ x: p.log_gbar, y: 1 })), '#64748b', [2, 2]),
  ], 'Mass discrepancy', 'log10 g_bar (m/s^2)', 'g_obs / g_bar');
  // GeoNum-tracked RAR ratio in the deep-MOND regime, operands in units of a0
  // (scaled to O(1) so GeoNum stays well-conditioned).
  const lo = d[0];
  const ratio = new GeoNum(lo.g_rar / a0).divide(new GeoNum(lo.g_bar / a0));
  out('tg-rar-out', table([
    ['Empirical fit (McGaugh 2016)', 'g_obs = g_bar / (1 - exp(-sqrt(g_bar/a0)))'],
    ['a0', r.a0_display],
    ['Transition acceleration', r.transition_region],
    ['Deep-MOND g_obs/g_bar (low g)', ratio.toFloat64().toFixed(2) + 'x'],
    ['High-acceleration limit', 'g_obs -> g_bar (Newtonian)'],
    ['Sampled points', d.length],
  ]) + geonumBadge(ratio, 'g_obs/g_bar'));
  setText('tg-rar-status', `a0 = ${r.a0_display} | transition at ${r.transition_region}`);
}

// ---------------------------------------------------------------- Dark Matter (NFW)
function runNfwTool(e) {
  e && e.preventDefault();
  const r = runDarkMatter({
    halo_mass: num('tg-nfw-halo', 1) * 1e12, concentration: num('tg-nfw-conc', 10),
    baryonic_mass: num('tg-nfw-bary', 5) * 1e10, scale_radius: num('tg-nfw-rs', 20),
  });
  const d = r.data, last = d[d.length - 1];
  chart('tg-nfw-rot', [
    line('LCDM (baryons + NFW halo)', d.map(p => ({ x: p.r_kpc, y: p.v_lcdm })), '#7c3aed'),
    line('Newtonian (baryons only)', d.map(p => ({ x: p.r_kpc, y: p.v_newton })), '#dc2626', [5, 4]),
  ], 'Rotation curve', 'Radius (kpc)', 'v (km/s)');
  chart('tg-nfw-rho', [
    line('rho_NFW', d.map(p => ({ x: p.r_kpc, y: Math.log10(Math.max(1e-30, p.rho_nfw)) })), '#059669'),
  ], 'NFW density profile', 'Radius (kpc)', 'log10 rho (Msun/kpc^3)');
  // GeoNum-tracked dynamical/baryonic mass ratio at the outer radius (operands
  // scaled to O(1) so GeoNum stays well-conditioned; ratio is scale-invariant).
  const mratio = new GeoNum(last.M_total_lcdm / 1e10).divide(new GeoNum(Math.max(1e-30, last.M_bary_enclosed) / 1e10));
  out('tg-nfw-out', table([
    ['Halo profile', 'rho(r) = rho_0 / [ (r/r_s)(1 + r/r_s)^2 ]  (NFW 1997)'],
    ['v_flat (LCDM, baryons+halo)', r.v_flat_lcdm + ' km/s'],
    ['v_flat (Newtonian, baryons)', r.v_flat_newton + ' km/s'],
    ['Halo mass', r.halo_mass + ' Msun'],
    ['Concentration c', r.concentration],
    ['Scale radius r_s', r.scale_radius + ' kpc'],
    ['Dark-matter fraction (outer)', r.dm_fraction + '%'],
    ['Dynamical/baryonic mass (outer)', mratio.toFloat64().toFixed(1) + 'x'],
  ]) + geonumBadge(mratio, 'M_dyn/M_bary'));
  setText('tg-nfw-status', `v_flat(LCDM) = ${r.v_flat_lcdm} km/s | DM fraction (outer) = ${r.dm_fraction}%`);
}

// ---------------------------------------------------------------- Hawking
function runHawkTool(e) {
  e && e.preventDefault();
  const r = runHawking({ black_hole_mass: num('tg-hawk-mass', 1e6), spin_parameter: num('tg-hawk-spin', 0.9) });
  chart('tg-hawk-evap', [
    line('T(t)', r.data.map(p => ({ x: p.t_fraction * 100, y: Math.log10(Math.max(1e-30, p.T_kelvin)) })), '#db2777'),
  ], 'Evaporation: temperature rise', '% of evaporation', 'log10 T (K)');
  chart('tg-hawk-masschart', [
    line('log T', r.mass_comparison.map(p => ({ x: p.mass_log, y: p.T_log })), '#ea580c'),
  ], 'Hawking T vs black-hole mass', 'log10 M (Msun)', 'log10 T (K)');
  // Hawking uses the engine's OWN GeoNum arithmetic (drift-tracked T_H).
  // `parseFloat(r.geo_drift) || 0` used to turn a MISSING geo_drift into 0, and 0
  // classifies EXACT - the best verdict manufactured from an absent measurement
  // (the same `|| 0` class fixed at js/molgen-gpu.js:861 and js/hpc-dft.js:272).
  // verdictFromDrift (gti-ladder.js) returns null for a non-finite drift, and an
  // absence renders "not reported" in neutral grey, never a verdict.
  const drift = parseFloat(r.geo_drift);
  const hawkVerdict = verdictFromDrift(drift);
  const tcol = hawkVerdict ? verdictColor(hawkVerdict) : '#6b7280';
  const hawkDetail = hawkVerdict
    ? `T_H(GeoNum) = ${r.geo_temperature} K · rel.unc ${r.geo_relative_unc_pct} · drift ${r.geo_drift} · zone ${r.geo_zone} · shade ${r.geo_shade}`
    : 'the engine reported no GeoNum drift for this run, so no verdict is derivable';
  out('tg-hawk-out', table([
    ['Hawking T (Kerr)', r.T_hawking_kelvin + ' K'],
    ['Hawking T (Schwarzschild)', r.T_schwarzschild_kelvin + ' K'],
    ['Horizon radius', r.r_horizon_km + ' km'],
    ['Surface gravity', r.surface_gravity],
    ['Bekenstein-Hawking entropy', r.S_entropy_bits + ' bits'],
    ['Luminosity', r.L_hawking_watts + ' W'],
    ['Evaporation time', r.t_evaporation],
    ['Page time', r.page_time],
  ]) + badge(hawkVerdict || 'not reported', tcol, hawkDetail));
  setText('tg-hawk-status', `T_H = ${r.T_hawking_kelvin} K | t_evap = ${r.t_evaporation}`);
}

export function initTheoryGravity() {
  const bind = (formId, fn) => document.getElementById(formId)?.addEventListener('submit', fn);
  bind('f-tg-mond', runMondTool);
  bind('f-tg-rar', runRarTool);
  bind('f-tg-nfw', runNfwTool);
  bind('f-tg-hawk', runHawkTool);
}
